/**
 * Shared Indian Rupee (INR) Price Formatter
 * Formats numbers into ₹Cr, ₹Lakhs, or formatted thousands.
 */
export const formatPriceInINR = (amount?: number | null): string => {
  if (amount == null || isNaN(amount)) return "₹0";
  if (amount >= 10000000) {
    const cr = amount / 10000000;
    return `₹${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(2)} Cr`;
  }
  if (amount >= 100000) {
    const lakh = amount / 100000;
    return `₹${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(2)} Lakhs`;
  }
  return `₹${amount.toLocaleString("en-IN")}`;
};

/** ASCII-safe amount for Helvetica PDF content streams. */
export const formatPriceInINRAscii = (amount?: number | null): string =>
  formatPriceInINR(amount).replace(/₹/g, "Rs. ");

const escapePdfText = (value: string) =>
  value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const wrapPdfLine = (line: string, maxLen = 92): string[] => {
  const text = line.replace(/\r/g, "");
  if (text.length <= maxLen) return [text];
  const chunks: string[] = [];
  let rest = text;
  while (rest.length > maxLen) {
    let cut = rest.lastIndexOf(" ", maxLen);
    if (cut < maxLen * 0.5) cut = maxLen;
    chunks.push(rest.slice(0, cut));
    rest = rest.slice(cut).trimStart();
  }
  if (rest) chunks.push(rest);
  return chunks;
};

/** Structured line for branded / sectioned PDF letters. */
export type PdfLine =
  | string
  | {
      kind:
        | "title"
        | "subtitle"
        | "heading"
        | "body"
        | "muted"
        | "rule"
        | "spacer";
      text?: string;
    };

export type BuildPdfOptions = {
  /** CRM / company name drawn as letterhead when no title line is supplied. */
  brandTitle?: string;
};

type NormalizedPdfLine = {
  kind:
    | "title"
    | "subtitle"
    | "heading"
    | "body"
    | "muted"
    | "rule"
    | "spacer";
  text: string;
};

const normalizePdfLines = (
  lines: PdfLine[],
  options?: BuildPdfOptions,
): NormalizedPdfLine[] => {
  const out: NormalizedPdfLine[] = [];
  const hasTitle = lines.some(
    (l) => typeof l !== "string" && l.kind === "title",
  );
  if (!hasTitle && options?.brandTitle?.trim()) {
    out.push({ kind: "title", text: options.brandTitle.trim() });
    out.push({ kind: "rule", text: "" });
  }
  for (const line of lines) {
    if (typeof line === "string") {
      out.push({ kind: "body", text: line });
    } else {
      out.push({ kind: line.kind, text: line.text ?? "" });
    }
  }
  return out;
};

/**
 * Build a single-page PDF (Type1 Helvetica / Helvetica-Bold — keep text ASCII).
 * Accepts plain strings (legacy) or structured lines for letterhead / sections.
 */
export function buildSimplePdfBlob(
  lines: PdfLine[],
  options?: BuildPdfOptions,
): Blob {
  const normalized = normalizePdfLines(lines, options);
  const streamOps: string[] = ["BT"];
  let y = 770;

  for (const line of normalized) {
    if (y < 48) break;

    if (line.kind === "spacer") {
      y -= 10;
      continue;
    }
    if (line.kind === "rule") {
      streamOps.push("ET");
      streamOps.push("0.55 0.55 0.55 RG");
      streamOps.push("1.2 w");
      streamOps.push(`48 ${y} m 564 ${y} l S`);
      streamOps.push("0 0 0 RG");
      streamOps.push("BT");
      y -= 16;
      continue;
    }

    let useFont: "F1" | "F2" = "F1";
    let useSize = 10;
    let useLeading = 13;
    let maxLen = 92;
    if (line.kind === "title") {
      useFont = "F2";
      useSize = 16;
      useLeading = 20;
      maxLen = 60;
    } else if (line.kind === "subtitle") {
      useFont = "F2";
      useSize = 12;
      useLeading = 16;
      maxLen = 72;
    } else if (line.kind === "heading") {
      useFont = "F2";
      useSize = 11;
      useLeading = 15;
      maxLen = 80;
    } else if (line.kind === "muted") {
      useFont = "F1";
      useSize = 9;
      useLeading = 12;
      maxLen = 96;
    }

    const chunks = line.text === "" ? [""] : wrapPdfLine(line.text, maxLen);
    for (const chunk of chunks) {
      if (y < 48) break;
      streamOps.push(`/${useFont} ${useSize} Tf`);
      streamOps.push(`1 0 0 1 48 ${y} Tm (${escapePdfText(chunk)}) Tj`);
      y -= useLeading;
    }
  }
  streamOps.push("ET");

  const stream = streamOps.join("\n");
  const streamLen = new TextEncoder().encode(stream).length;

  const objects = [
    "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n",
    "2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n",
    "3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> >>\nendobj\n",
    `4 0 obj\n<< /Length ${streamLen} >>\nstream\n${stream}\nendstream\nendobj\n`,
    "5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n",
    "6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n",
  ];

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (const obj of objects) {
    offsets.push(new TextEncoder().encode(pdf).length);
    pdf += obj;
  }
  const xrefStart = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  for (let i = 1; i <= objects.length; i++) {
    pdf += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  pdf += `startxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
}

export function downloadSimplePdf(
  filename: string,
  lines: PdfLine[],
  options?: BuildPdfOptions,
): void {
  const blob = buildSimplePdfBlob(lines, options);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
